/**
 * Template Issues — what the backend could not read, or had to assume, while
 * parsing an EFDA or Communication Template
 * (backend: docs/features/template-upload-error-recovery/, `templateIssues.ts`).
 *
 * A parse result carries `issues[]` beside `emissionsData[]`:
 *   - file issue (`error`/`file`): no usable emissions from this file until resolved
 *   - row issue  (`error`/`row`):  that goods row was dropped, the others kept
 *   - warning:                     nothing dropped; the parser substituted a value
 *
 * Some issues carry a `resolution`: the user settles them here instead of in the
 * workbook.
 *   - 'reportingPeriod' — the rows came back with `reportingPeriod: null`; the
 *     user supplies the period (`applyReportingPeriod`).
 *   - 'indirectChoice' — a row's stated indirect emissions contradict electricity
 *     consumed × factor; the user picks one figure.
 *   - 'duplicateChoice' — the same good appears in several rows with different
 *     figures; the user keeps one row.
 * `resolveTemplate` applies all of them; `awaitsInput` says whether any is left.
 *
 * Everything in this file is pure; the components beside it render it.
 */
import i18n from 'i18n';
import { renderIssue } from 'helpers/serverIssues';

const RESOLUTION_REPORTING_PERIOD = 'reportingPeriod';

const issuesOf = (parsedData) => (Array.isArray(parsedData?.issues) ? parsedData.issues : []);

const isPeriodIssue = (issue) => issue.resolution === RESOLUTION_REPORTING_PERIOD;

const CHOICE_RESOLUTIONS = new Set(['indirectChoice', 'duplicateChoice']);
const isChoiceIssue = (issue) => CHOICE_RESOLUTIONS.has(issue.resolution);

/**
 * Split a parse result's issues by what the user has to do about them.
 *
 * `resolvable: false` is for a surface that cannot take a period or a choice
 * (the supplier portal rejects such files): there every issue is something to
 * correct in the workbook, so period issues count as file errors and choice
 * issues as row errors.
 */
export function partitionTemplateIssues(parsedData, { resolvable = true } = {}) {
  const issues = issuesOf(parsedData);
  const errors = issues.filter((issue) => issue.severity === 'error');
  if (!resolvable) {
    return {
      fileErrors: errors.filter((issue) => issue.scope === 'file'),
      periodIssues: [],
      rowErrors: errors.filter((issue) => issue.scope === 'row'),
      choiceIssues: [],
      warnings: issues.filter((issue) => issue.severity === 'warning'),
    };
  }
  return {
    /** The file cannot be used until the workbook is corrected. */
    fileErrors: errors.filter((issue) => issue.scope === 'file' && !isPeriodIssue(issue)),
    /** The reporting period is missing or unreadable — fixable here. */
    periodIssues: errors.filter(isPeriodIssue),
    /** Rows that were dropped; the rest of the file is usable. */
    rowErrors: errors.filter((issue) => issue.scope === 'row' && !isChoiceIssue(issue)),
    /** Rows kept, but waiting for the user to choose between figures. */
    choiceIssues: errors.filter(isChoiceIssue),
    warnings: issues.filter((issue) => issue.severity === 'warning'),
  };
}

/**
 * True when nothing can be saved from this file as it stands: a file issue the
 * user cannot resolve here, or no rows left.
 */
export function blocksEmissions(parsedData) {
  return partitionTemplateIssues(parsedData).fileErrors.length > 0;
}

/** `'2025-01-01'` (a date input's value) or a Date/ISO string → ISO datetime, UTC midnight. */
export function toUtcMidnightIso(value) {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${value}T00:00:00.000Z`;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** A period is usable when both ends are dates and the start is not after the end. */
export function isValidReportingPeriod(period) {
  const from = toUtcMidnightIso(period?.from);
  const to = toUtcMidnightIso(period?.to);
  return Boolean(from && to && from <= to);
}

/**
 * Apply a period the user entered to a parse result: every row without a period
 * gets it, and the issue asking for it is dropped. A row that already has a
 * period keeps it — the workbook's own statement always wins.
 *
 * Returns a new object; the input is not mutated.
 */
export function applyReportingPeriod(parsedData, period) {
  if (!parsedData || !isValidReportingPeriod(period)) return parsedData;
  const reportingPeriod = { from: toUtcMidnightIso(period.from), to: toUtcMidnightIso(period.to) };
  return {
    ...parsedData,
    emissionsData: (parsedData.emissionsData || []).map((row) =>
      row.reportingPeriod ? row : { ...row, reportingPeriod },
    ),
    issues: issuesOf(parsedData).filter((issue) => !isPeriodIssue(issue)),
  };
}

// The `field` param of a row issue is the backend's property name. Shown to a
// supplier it should read as the column they filled in.
const FIELD_LABELS = {
  // t('common:templateIssues.fields.directEmission', 'direct emissions')
  directEmission: 'direct emissions',
  // t('common:templateIssues.fields.indirectEmission', 'indirect emissions')
  indirectEmission: 'indirect emissions',
  // t('common:templateIssues.fields.electricityConsumed', 'electricity consumed')
  electricityConsumed: 'electricity consumed',
  // t('common:templateIssues.fields.electricityEmissionFactor', 'electricity emission factor')
  electricityEmissionFactor: 'electricity emission factor',
};

const fieldLabel = (field) =>
  FIELD_LABELS[field]
    ? i18n.t('common:templateIssues.fields.' + field, FIELD_LABELS[field])
    : field;

/** One issue as a sentence in the active locale; the backend's English is the fallback. */
export function renderTemplateIssue(issue) {
  if (!issue) return '';
  const params = { ...issue.params };
  if (typeof params.field === 'string') params.field = fieldLabel(params.field);
  if (Array.isArray(params.fields)) params.fields = params.fields.map(fieldLabel);
  return renderIssue({ ...issue, params }) || issue.message || issue.code;
}

const INDIRECT_KEY_PREFIX = 'indirect:';

/** The key a choice is stored under: one per issue, stable across renders. */
export function choiceKey(issue) {
  if (issue.resolution === 'indirectChoice') return `${INDIRECT_KEY_PREFIX}${issue.params?.row}`;
  return `duplicate:${(issue.params?.rows || []).join(',')}`;
}

// A choice about a row that a duplicate choice has since dropped no longer
// matters; asking it would hold the upload on a question about nothing.
const concernsPresentRow = (rows) => {
  const present = new Set(rows.map((row) => row.sourceRow));
  return (issue) => issue.resolution !== 'indirectChoice' || present.has(issue.params?.row);
};

/**
 * Apply the choices the user made. `choices` maps `choiceKey(issue)` to
 * `'calculated' | 'stated'` for an indirect choice, or to the Excel row to keep
 * for a duplicate group. An issue without a choice is left as it is.
 *
 * Returns a new object; the input is not mutated.
 */
export function applyRowChoices(parsedData, choices = {}) {
  if (!parsedData) return parsedData;
  let rows = parsedData.emissionsData || [];
  const remaining = [];
  for (const issue of issuesOf(parsedData)) {
    const choice = isChoiceIssue(issue) ? choices[choiceKey(issue)] : undefined;
    if (choice === undefined) {
      remaining.push(issue);
    } else if (issue.resolution === 'indirectChoice') {
      if (choice === 'stated') {
        rows = rows.map((row) =>
          row.sourceRow === issue.params?.row && row.indirectEmissionStated !== undefined
            ? { ...row, indirectEmission: row.indirectEmissionStated }
            : row,
        );
      }
    } else {
      const dropped = new Set((issue.params?.rows || []).filter((row) => row !== choice));
      rows = rows.filter((row) => !dropped.has(row.sourceRow));
    }
  }
  return { ...parsedData, emissionsData: rows, issues: remaining.filter(concernsPresentRow(rows)) };
}

/**
 * The choices to show, answered ones included, so an answer stays on screen.
 * Each is `{ issue, key, choice, candidates }`: `choice` is the answer so far,
 * and `candidates` (duplicate choices only) are the rows on offer, with the
 * indirect figure already chosen for each.
 */
export function choiceQuestions(parsedData, choices = {}) {
  const resolvedRows = applyRowChoices(parsedData, choices)?.emissionsData || [];
  const indirectChoices = Object.fromEntries(
    Object.entries(choices).filter(([key]) => key.startsWith(INDIRECT_KEY_PREFIX)),
  );
  const offeredRows = applyRowChoices(parsedData, indirectChoices)?.emissionsData || [];
  return issuesOf(parsedData)
    .filter(isChoiceIssue)
    .filter(concernsPresentRow(resolvedRows))
    .map((issue) => {
      const key = choiceKey(issue);
      return {
        issue,
        key,
        choice: choices[key],
        candidates:
          issue.resolution === 'duplicateChoice'
            ? (issue.params?.rows || []).map((sourceRow) => {
                const row = offeredRows.find((candidate) => candidate.sourceRow === sourceRow);
                return {
                  sourceRow,
                  directEmission: row?.directEmission,
                  indirectEmission: row?.indirectEmission,
                };
              })
            : [],
      };
    });
}

/** The parse result with the user's period and choices applied. */
export function resolveTemplate(parsedData, { period, choices } = {}) {
  const withPeriod = period ? applyReportingPeriod(parsedData, period) : parsedData;
  return applyRowChoices(withPeriod, choices);
}

/** True while the rows still need something from the user before they can be saved. */
export function awaitsInput(parsedData) {
  return issuesOf(parsedData).some((issue) => isPeriodIssue(issue) || isChoiceIssue(issue));
}
